import React from 'react';
import { GameState, Player, GamePhase, CardColor } from '../types';
import { TowerCrystal } from '../constants';
import * as gameLogic from '../services/gameLogic';
import { Language, t } from '../data/translations';
import { useShellLayout } from '../hooks/useShellLayout';

const colorHexMap: Record<CardColor, string> = {
    [CardColor.Red]: '#ef4444', [CardColor.Pink]: '#ec4899', [CardColor.Green]: '#22c55e',
    [CardColor.Black]: '#64748b', [CardColor.Purple]: '#a855f7', [CardColor.Yellow]: '#eab308',
};

interface TowerOption {
    color: CardColor;
    source: 'neutral' | 'opponent';
    cost: number;
}

const TowerPurchaseModal: React.FC<{
    gameState: GameState;
    localPlayer: Player;
    phase: GamePhase;
    onPurchase: (color: CardColor, preferOpponent?: boolean) => void;
    onCancel: () => void;
    onPurifyTargetSelect: (color: CardColor) => void;
    language: Language;
}> = ({ gameState, localPlayer, phase, onPurchase, onCancel, onPurifyTargetSelect, language }) => {
    const opponent = gameState.players.find(p => p.id !== localPlayer.id)!;
    const { isMobileShell } = useShellLayout();
    const isPurifyMode = phase === GamePhase.AWAITING_PURIFICATION_TARGET;

    const neutralColors = gameState.availableTowers;
    const opponentColors = opponent.towers.map(t => t.color);
    const purifyColors = [...new Set([...neutralColors, ...opponentColors])];

    const towerOptions: TowerOption[] = isPurifyMode
        ? purifyColors.map(color => ({ color, source: 'neutral' as const, cost: 0 }))
        : (() => {
            const options: TowerOption[] = [];
            for (const color of neutralColors) {
                options.push({ color, source: 'neutral', cost: 3 });
            }
            for (const color of opponentColors) {
                options.push({ color, source: 'opponent', cost: 4 });
            }
            return options;
        })();

    return (
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-40">
            <div className={`flex items-center justify-center ${isMobileShell ? 'flex-row gap-8 scale-[0.85]' : 'flex-col gap-0'}`}>
                <div className={`flex flex-col items-center justify-center ${isMobileShell ? 'w-1/2 text-left' : 'w-full text-center'}`}>
                    <h2 className="text-4xl font-bold mb-4 text-slate-200 text-shadow-lg leading-tight">
                        {isPurifyMode ? t('ui.choosePurificationTarget', language) : t('ui.acquireTower', language)}
                    </h2>
                    <p className={`text-slate-400 max-w-md ${isMobileShell ? 'mb-4' : 'mb-8'}`}>
                        {isPurifyMode ? t('ui.purificationDesc', language) : t('ui.towerPurchaseDesc', language)}
                    </p>
                    {isMobileShell && !isPurifyMode && (
                        <button
                            onClick={onCancel}
                            className="bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-300 w-full max-w-[200px]"
                        >
                            {t('ui.cancel', language)}
                        </button>
                    )}
                </div>
                
                <div className={`grid grid-cols-3 gap-8 ${isMobileShell ? 'w-1/2' : 'mb-8'}`}>
                    {towerOptions.map((opt, idx) => {
                        const canAfford = isPurifyMode ? true : localPlayer.hand.filter(c => c.color === opt.color && !c.essenceCost).length >= opt.cost;

                        return (
                            <div key={`${opt.color}-${opt.source}-${idx}`}
                                className={`flex flex-col items-center justify-center gap-3 p-4 transition-all duration-300 ${canAfford ? 'cursor-pointer transform hover:scale-110' : 'opacity-40'}`}
                                onClick={canAfford ? () => (isPurifyMode ? onPurifyTargetSelect(opt.color) : onPurchase(opt.color, opt.source === 'opponent')) : undefined}
                            >
                                <TowerCrystal color={opt.color} className="w-20 h-20" />
                                <span className="font-bold text-lg" style={{ color: colorHexMap[opt.color] }}>{t(`color.${opt.color.toLowerCase()}` as any, language)}</span>
                                {!isPurifyMode && (
                                    <div className="text-sm text-slate-300 whitespace-nowrap">
                                        <span className={`${opt.source === 'opponent' ? 'text-orange-400' : 'text-sky-400'}`}>{opt.source === 'opponent' ? t('ui.stealFromOpponent', language) : t('ui.claimNeutral', language)}</span> · {t('ui.cost', language)}{opt.cost} <span style={{ color: colorHexMap[opt.color] }}>{t(`color.${opt.color.toLowerCase()}` as any, language)}</span> {t('ui.cardsSuffix', language)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            
            {!isMobileShell && !isPurifyMode && (
                <button
                    onClick={onCancel}
                    className="bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-300 mt-4"
                >
                    {t('ui.cancel', language)}
                </button>
            )}
        </div>
    );
};

export default TowerPurchaseModal;
